import React from 'react';
import { Forms, Loading, Paging, SystemText } from '@salesperformanceint/e4shared';
import { useDispatch, useSelector } from 'react-redux';
import { apiGet } from 'services';
import config from '../../../../../../config';
import { setGoogleAuthToken } from 'actions/user';

export const YouTube = ({ assets, allowMultiple, onAdd }) => {
    const dispatch = useDispatch();

    const [results, setResults] = React.useState([]);
    const [items, setItems] = React.useState(assets);
    const [search, setSearch] = React.useState(null);
    const [page, setPage] = React.useState(1);
    const [googleClient, setGoogleClient] = React.useState(null);
    const [error, setError] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [doneSearch, setDoneSearch] = React.useState(false);
    const guid = useSelector(state => (state.user.user ? state.user.user.guid : ''));
    const googleAuth = useSelector(state => state.user.googleAuthToken);

    React.useEffect(() => {
        const client = google.accounts.oauth2.initTokenClient({
            client_id: config.google.clientId,
            scope: config.google.scope.join(' '),
            callback: tokenResponse => {
                dispatch(setGoogleAuthToken(tokenResponse.access_token));
            },
        });

        setGoogleClient(client);
    }, []);

    const getToken = () => {
        googleClient?.requestAccessToken();
    };

    const revokeToken = () => {
        google.accounts.oauth2.revoke(googleAuth, () => {
            dispatch(setGoogleAuthToken(null));
        });
    };

    const pageSize = 4;

    const runSearch = () => {
        setLoading(true);
        setError('');
        apiGet(
            `https://www.googleapis.com/youtube/v3/search?part=snippet&type=video&videoEmbeddable=true&q=${search}&safeSearch=strict&maxResults=50`,
            guid,
            response => {
                if (response.error) {
                    setError('There was a problem calling YouTube');
                } else {
                    setResults(
                        response.items.map(i => {
                            return {
                                name: i.snippet.title,
                                description: i.snippet.description,
                                id: i.id,
                                thumb: i.snippet.thumbnails.default.url,
                            };
                        })
                    );
                }

                setPage(1);
                setDoneSearch(true);
                setLoading(false);
            },
            err => {
                setError('There was a problem calling YouTube');
                setLoading(false);
            },
            false,
            new Headers({ Accept: 'application/json', Authorization: `Bearer ${googleAuth}` })
        );
    };

    React.useEffect(() => {
        items && onAdd(items, 'youtube');
    }, [items]);

    const addItem = add => {
        add.id = add.id ? add.id.videoId : {};
        if (allowMultiple) {
            if (!items.some(i => i.internal_Id === add.id.videoId)) {
                setItems(items.concat([{ ...add, internal_Id: add.id }]));
            }
        } else {
            setItems([{ ...add, internal_Id: add.id }]);
        }
    };

    const removeItem = remove => {
        setItems(items.filter(i => i.internal_Id !== remove.internal_Id));
    };

    const st = (path, ...rest) => SystemText(`General/Controls/FilePicker/YouTube/${path}`, ...rest);

    if (loading) return <Loading />;

    if (!googleAuth)
        return (
            <div className="text-center mt100">
                <button
                    data-testid="google-login-button"
                    id="google-login-button"
                    className="btn btn-primary"
                    onClick={getToken}
                >
                    <i className="fa fa-google mr5" />
                    {st('Login')}
                </button>
            </div>
        );

    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    const Card = ({ result }) => {
        return (
            <div key={`${result.id}`} className="flex mb10 pitch-card pointer" onClick={() => addItem(result)}>
                <div className="asset-thumb">
                    <img src={result.thumb} alt="" loading="lazy" />
                </div>
                <div className="flex-grow ml10">
                    <label>{result.name}</label>
                    <div className="text-sm">{result.description}</div>
                </div>
            </div>
        );
    };

    return (
        <div className="">
            <div>
                <Forms.Text
                    data-testid="filter-search"
                    id="filter-search"
                    label={st('Label')}
                    placeholder={st('Placeholder')}
                    value={search}
                    error={error}
                    onChange={x => setSearch(x)}
                    onSubmit={runSearch}
                    icon="fa-search"
                />
            </div>
            <div className="mb10">
                <button data-testid="search-button" id="search-button" className="btn btn-primary" onClick={runSearch}>
                    {st('Search')}
                </button>
            </div>
            {results.length === 0 && doneSearch && <div>{st('NoResults')}</div>}
            {results.length > 0 && (
                <div>
                    <div>
                        <label>{st('Results')}</label>
                        <span className="text-sm ml10">({st('Select')})</span>
                    </div>
                    <div className="list">
                        {results.map((t, i) => (i < start || i > end ? null : <Card key={`card-${i}`} result={t} />))}
                    </div>
                    {results.length > 0 && (
                        <Paging
                            pageSize={pageSize}
                            recordCount={results.length}
                            page={page}
                            goToPage={page => setPage(page)}
                        />
                    )}
                </div>
            )}
            {items.length > 0 && (
                <div className="mt20">
                    <label>{st('Selected')}</label>
                    {items
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                        .map((u, i) => {
                            return (
                                <div key={`file-${i}`} className="mb5 flex flex-center">
                                    <i className={`fa fa-youtube-play mr5`} />
                                    <span className="ellipsis500 flex-grow">{u.name}</span>
                                    <i
                                        className="ml5 text-danger fa fa-trash pointer text-right"
                                        title={st('Remove', u.name)}
                                        onClick={() => removeItem(u)}
                                    />
                                </div>
                            );
                        })}
                </div>
            )}

            {googleAuth && (
                <div className="mt20">
                    <button id="google-logout-button" className="btn btn-link btn-xs mt20" onClick={revokeToken}>
                        <span>{st('Logout')}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default YouTube;
